import { Typography } from '@mui/material';
import { memo, useMemo, useCallback } from 'react';
import { usePrompt } from '@hooks/usePrompt';
import { Save } from '@mui/icons-material';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { EventForm } from '@components/eventCreation/CreateEventUtils';
import { addUpdateEvent } from '@modules/supabase/events';
import { toast } from 'react-toastify';
import { breadcrumb } from '@utils/logging';
import FAB from '@atoms/FAB';

const SubmitGeneralEventInfoFAB = () => {
    const {
        handleSubmit,
        reset,
        formState: { isDirty, errors },
    } = useFormContext<EventForm>();

    const onSubmit: SubmitHandler<EventForm> = useCallback(
        (values) =>
            toast.promise(
                async () => {
                    breadcrumb('[submit]: addUpdateEvent', { values });

                    const updatedEvent = await addUpdateEvent(values);
                    reset(updatedEvent || values);
                },
                {
                    pending: 'Updating divisions...',
                    success: 'Divisions updated',
                    error: 'Error updating divisions',
                },
            ),
        [reset],
    );

    const hasErrors = useMemo(() => {
        if (errors && Object.keys(errors).length > 0) {
            return !!errors;
        } else {
            return false;
        }
    }, [errors]);

    usePrompt(
        `You have unsaved changes which will be lost. \nAre you sure you want to leave?`,
        isDirty,
    );

    return (
        <FAB
            color="primary"
            disabled={!isDirty}
            onClick={() => {
                if (
                    hasErrors &&
                    !window.confirm(
                        `Are you sure you are ready to save? Event has some invalid data`,
                    )
                ) {
                    console.log(`user cancelled save`);
                    return;
                } else {
                    handleSubmit(onSubmit, () =>
                        toast.error(`Invalid event details, check entered values`),
                    )();
                }
            }}
            toolTip={hasErrors ? <RenderError /> : 'Save Event Details'}
        >
            <Save />
        </FAB>
    );
};

export default memo(SubmitGeneralEventInfoFAB);

function RenderError() {
    const {
        formState: { errors },
    } = useFormContext<EventForm>();

    return (
        [
            'birthdayCutoff',
            'name',
            'description',
            'fee',
            'imageUrl',
            'location',
            'maxEntries',
            'placeId',
            'startAt',
            'waiver',
            'weighInAt',
            'weightTolerance',
        ] as (keyof EventForm)[]
    ).map((key) => {
        return errors[key]?.message ? <Typography>{errors[key]?.message}</Typography> : null;
    });
}
